/* Copyright (c) 2023-2024 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { ReactNode } from "react";
import { When, Else, If, Then } from "react-if";
import { Link } from "react-router-dom";
import {
  Box,
  FlexContainer,
  SVGIcon,
  SVGIconProps,
  Text,
  theme,
} from "@nordcloud/gnui";
import { isNotNil } from "~/tools";
import { ChildrenWrap } from "../styles";

type Props = {
  icon: SVGIconProps["name"];
  title: string;
  value: number;
  iconLink?: string;
  flex?: number;
  children?: ReactNode;
};

export function DataBox({
  icon,
  title,
  value,
  iconLink,
  flex = 1,
  children,
}: Props) {
  return (
    <Box boxStyle="lightGrey" css={{ flex }}>
      <FlexContainer alignItems="flex-start" gap={theme.spacing.spacing02}>
        <FlexContainer
          direction="column"
          gap={theme.spacing.spacing03}
          alignItems="flex-start"
          marginRight={theme.spacing.spacing07}
        >
          <If condition={isNotNil(iconLink)}>
            <Then>
              <Link to={iconLink ?? "."}>
                <FlexContainer gap={theme.spacing.spacing02}>
                  <SVGIcon
                    name={icon}
                    css={{ fill: theme.color.interactive.link }}
                  />
                  <Text
                    mb={0}
                    css={{ textWrap: "nowrap" }}
                    color={theme.color.interactive.link}
                  >
                    {title}
                  </Text>
                </FlexContainer>
              </Link>
            </Then>
            <Else>
              <FlexContainer gap={theme.spacing.spacing02}>
                <SVGIcon name={icon} />
                <Text mb={0} css={{ textWrap: "nowrap" }}>
                  {title}
                </Text>
              </FlexContainer>
            </Else>
          </If>

          {/* margin-left to vertical align with text above = icon size (1.5rem) + gap (0.5rem) */}
          <Text size="lg" weight="bold" mb={0} ml={theme.spacing.spacing07}>
            {value}
          </Text>
        </FlexContainer>

        <When condition={isNotNil(children)}>
          <ChildrenWrap>{children}</ChildrenWrap>
        </When>
      </FlexContainer>
    </Box>
  );
}
